import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider } from 'react-hook-form';

import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  Divider,
  DialogTitle,
  DialogActions,
  DialogContent,
  InputAdornment,
} from '@mui/material';

import {
  ShopifyStoreIntegrationType,
  useGetShopifyOauthUrlLazyQuery,
  useSetShopifyIntegrationMutation,
  SetShopifyIntegrationMutationVariables,
} from 'src/graphql/generated';

import { RHFTextField } from 'src/components/hook-form';

import {
  ShopifyFormSchema,
  shopifyConfigFormSchemaDefaultValues,
} from '../schema/shopify-form-schema';

// ----------------------------------------------------------------------

const SHOPIFY_STATE_KEY = 'shopify_oauth_state';

// ----------------------------------------------------------------------

type TAttributes = 'baseUrl' | 'accessToken' | 'activated';

type TCurrentData = Pick<ShopifyStoreIntegrationType, TAttributes>;

type Props = {
  open: boolean;
  onClose: () => void;
  disableForm?: boolean;
  currentData?: TCurrentData | null;
  refetch: () => Promise<ApolloQueryResult<any>>;
  code?: string | null;
  shop?: string | null;
};

export function ConfigShopifyModal({
  open,
  onClose,
  disableForm,
  currentData,
  refetch,
  code,
  shop,
}: Props) {
  const isCallbackMode = Boolean(code && shop);
  const [enableForm, setEnableForm] = useState<boolean>(!disableForm && !isCallbackMode);

  const { enqueueSnackbar } = useSnackbar();
  const [getOauthUrl] = useGetShopifyOauthUrlLazyQuery();
  const [setShopifyIntegration, { loading }] = useSetShopifyIntegrationMutation();

  const methods = useForm({
    resolver: yupResolver(ShopifyFormSchema),
    defaultValues: shopifyConfigFormSchemaDefaultValues,
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  const handleClose = () => {
    onClose();
    reset();
    setEnableForm(!disableForm && !isCallbackMode);
  };

  const handleRedirect = handleSubmit(async ({ baseUrl }) => {
    const shopDomain = `${baseUrl}.myshopify.com`;
    const { data } = await getOauthUrl({ variables: { shop: shopDomain } });
    const url = data?.shopifyOauthUrl;
    if (!url) return;

    const state = new URL(url).searchParams.get('state');
    if (state) sessionStorage.setItem(SHOPIFY_STATE_KEY, state);

    window.location.href = url;
  });

  // Callback mode: send authorization code to backend for token exchange
  const handleSaveCallback = async () => {
    if (!code || !shop) return;

    const variables: SetShopifyIntegrationMutationVariables = {
      input: {
        baseUrl: shop,
        code,
        activated: true,
      },
    };

    try {
      const { data: response } = await setShopifyIntegration({ variables });

      if (response?.setShopifyStoreIntegration?.ok === true) {
        enqueueSnackbar('Integração Shopify realizada com sucesso!', { variant: 'success' });
      } else {
        enqueueSnackbar('Erro ao salvar os dados da integração.', { variant: 'error' });
      }

      onClose();
      await refetch();
    } catch (error) {
      enqueueSnackbar('Não foi possível finalizar a integração.', { variant: 'error' });
    }
  };

  useEffect(() => {
    if (isCallbackMode && shop) {
      const shopName = shop.replace('.myshopify.com', '');
      reset({ baseUrl: shopName });
      return;
    }

    if (currentData?.baseUrl) {
      const shopName = currentData.baseUrl.replace('.myshopify.com', '');
      reset({ baseUrl: shopName });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentData, isCallbackMode, shop]);

  useEffect(() => {
    setEnableForm(!disableForm && !isCallbackMode);
  }, [disableForm, isCallbackMode]);

  return (
    <FormProvider {...methods}>
      <Dialog
        open={open}
        PaperProps={{
          sx: {
            minWidth: { xs: '90%', md: 400 },
          },
        }}
      >
        <DialogContent
          sx={{
            minHeight: 180,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: 2,
            marginBottom: 2,
            py: 2,
          }}
        >
          <DialogTitle textAlign="center" fontSize={20}>
            Integração Shopify
          </DialogTitle>

          <RHFTextField
            name="baseUrl"
            label="URL da Loja"
            disabled={!enableForm}
            FormHelperTextProps={{
              sx: {
                fontWeight: 600,
              },
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">.myshopify.com</InputAdornment>,
            }}
          />
        </DialogContent>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={handleClose}>
            Fechar
          </Button>

          {isCallbackMode && (
            <LoadingButton
              variant="contained"
              color="primary"
              loading={isSubmitting || loading}
              onClick={handleSaveCallback}
            >
              Salvar
            </LoadingButton>
          )}

          {!isCallbackMode && enableForm && (
            <LoadingButton
              variant="contained"
              color="primary"
              loading={isSubmitting}
              onClick={handleRedirect}
            >
              Conectar
            </LoadingButton>
          )}

          {!isCallbackMode && !enableForm && (
            <Button variant="contained" color="primary" onClick={() => setEnableForm(true)}>
              {currentData ? 'Editar' : 'Criar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
